/**
 * Centralised environment + endpoint configuration.
 *
 * The ONE place where `import.meta.env` is read and where every backend
 * endpoint path lives. Nothing else in the app should hard-code a URL or read a
 * raw env var — import `environment` and `endpoints` from here.
 *
 * Base URL shape (from the API contract):
 *   {edge-service}/order-fastrr/api/ve1/fastrr-return
 *
 * This app is the MERCHANT admin surface. All merchant endpoints require the
 * `x-auth-token` (Fastrr dashboard session token) and `x-merchant` headers,
 * attached centrally in src/api/client.ts.
 */

function readEnv(key: string, fallback = ''): string {
  const value = import.meta.env[key as keyof ImportMetaEnv];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function readNumberEnv(key: string, fallback: number): number {
  const parsed = Number(readEnv(key));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function withoutTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function withLeadingSlash(value: string): string {
  if (!value) {
    return '';
  }
  return value.startsWith('/') ? value : `/${value}`;
}

/** Fixed suffix from the API contract — same across every environment. */
const API_BASE_PATH = '/api/ve1/fastrr-return';

export const environment = {
  apiBaseUrl:
    withoutTrailingSlash(readEnv('VITE_API_EDGE_URL')) +
    withLeadingSlash(withoutTrailingSlash(readEnv('VITE_API_PATH_URL'))) +
    API_BASE_PATH,
  defaultMerchantDomain: readEnv('VITE_DEFAULT_MERCHANT_DOMAIN'),
  defaultAuthToken: readEnv('VITE_DEFAULT_AUTH_TOKEN'),
  apiTimeoutMs: readNumberEnv('VITE_API_TIMEOUT_MS', 30000),
  isProduction: import.meta.env.PROD,
} as const;

/** Build a querystring from a record, skipping null/undefined/empty values. */
function queryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const serialised = search.toString();
  return serialised ? `?${serialised}` : '';
}

export interface ReturnsQuery {
  status?: string;
  statuses?: string;
  refundMode?: string;
  isExchange?: boolean;
  page?: number;
  size?: number;
  from?: string;
  to?: string;
}

/** Merchant endpoint path builders, relative to `environment.apiBaseUrl`. */
export const endpoints = {
  // ── Dashboard & analytics (from/to are local YYYY-MM-DD) ──────────────────
  dashboard: (range: { from?: string; to?: string } = {}) =>
    `/merchant/dashboard${queryString({ from: range.from, to: range.to })}`,
  analytics: (range: { from?: string; to?: string } = {}) =>
    `/merchant/analytics${queryString({ from: range.from, to: range.to })}`,
  /** Server-generated CSV export for a screen `type` over a date range. */
  analyticsExport: (params: { from: string; to: string; type: string; status?: string; statuses?: string }) =>
    `/merchant/analytics/export${queryString({
      from: params.from,
      to: params.to,
      type: params.type,
      status: params.status,
      statuses: params.statuses,
    })}`,

  // ── Session (exchange the dashboard token for a session-info handle) ──────
  session: () => '/merchant/session',

  // ── Settings (policy, refund modes, notifications, branding, go-live) ─────
  settings: () => '/merchant/settings',

  // ── Return reasons ────────────────────────────────────────────────────────
  reasons: () => '/merchant/settings/reasons',
  reason: (slug: string) => `/merchant/settings/reasons/${encodeURIComponent(slug)}`,
  reasonsReorder: () => '/merchant/settings/reasons/reorder',

  // ── Warehouses ────────────────────────────────────────────────────────────
  warehouses: () => '/merchant/warehouses',
  warehouse: (warehouseId: string) => `/merchant/warehouses/${encodeURIComponent(warehouseId)}`,

  // ── Postcode lookup ────────────────────────────────────────────────────────
  postcodeDetails: (postcode: string) => `/merchant/postcode/details${queryString({ postcode })}`,

  // ── Returns ───────────────────────────────────────────────────────────────
  returns: (query: ReturnsQuery = {}) =>
    `/merchant/returns${queryString({
      status: query.status,
      statuses: query.statuses,
      refundMode: query.refundMode,
      isExchange: query.isExchange,
      page: query.page,
      size: query.size,
      from: query.from,
      to: query.to,
    })}`,
  returnDetail: (returnId: string) => `/merchant/returns/${encodeURIComponent(returnId)}`,
  returnReview: (returnId: string) => `/merchant/returns/${encodeURIComponent(returnId)}/review`,
  returnQc: (returnId: string) => `/merchant/returns/${encodeURIComponent(returnId)}/qc`,
  returnSchedulePickup: (returnId: string) =>
    `/merchant/returns/${encodeURIComponent(returnId)}/schedule-pickup`,
  returnRefund: (returnId: string) => `/merchant/returns/${encodeURIComponent(returnId)}/refund`,
  returnPartialRefund: (returnId: string) =>
    `/merchant/returns/${encodeURIComponent(returnId)}/partial-refund`,
  returnShipBack: (returnId: string) => `/merchant/returns/${encodeURIComponent(returnId)}/ship-back`,
  returnPickupAddress: (returnId: string) =>
    `/merchant/returns/${encodeURIComponent(returnId)}/pickup-address`,

  // ── Exchanges & manual return creation ────────────────────────────────────
  exchangeStats: (range: { from?: string; to?: string } = {}) =>
    `/merchant/returns/exchange-stats${queryString({ from: range.from, to: range.to })}`,
  orderLookup: (orderNumber?: string, email?: string, phone?: string) =>
    `/merchant/returns/lookup${queryString({ orderNumber, email, phone })}`,
  manualReturn: () => '/merchant/returns/manual',

  // ── Refunds ───────────────────────────────────────────────────────────────
  refunds: (page = 0, size = 20, range: { from?: string; to?: string } = {}) =>
    `/merchant/refunds${queryString({ page, size, from: range.from, to: range.to })}`,

  // ── Exceptions ────────────────────────────────────────────────────────────
  exceptions: (page = 0, size = 20, range: { from?: string; to?: string } = {}) =>
    `/merchant/exceptions${queryString({ page, size, from: range.from, to: range.to })}`,
  exceptionResolve: (exceptionId: string) =>
    `/merchant/exceptions/${encodeURIComponent(exceptionId)}/resolve`,
  exceptionsSummary: (range: { from?: string; to?: string } = {}) =>
    `/merchant/exceptions/summary${queryString({ from: range.from, to: range.to })}`,

  // ── Autopilot rules ───────────────────────────────────────────────────────
  autopilotRules: () => '/merchant/autopilot/rules',
  autopilotRule: (ruleId: string) => `/merchant/autopilot/rules/${encodeURIComponent(ruleId)}`,
  autopilotAudit: (page = 0, size = 20) => `/merchant/autopilot/audit${queryString({ page, size })}`,

  // ── Blocklist ─────────────────────────────────────────────────────────────
  blocklist: (range: { from?: string; to?: string } = {}) =>
    `/merchant/blocklist${queryString({ from: range.from, to: range.to })}`,
  blocklistEntry: (blockId: string) => `/merchant/blocklist/${encodeURIComponent(blockId)}`,
  blocklistStats: (range: { from?: string; to?: string } = {}) =>
    `/merchant/blocklist/stats${queryString({ from: range.from, to: range.to })}`,

  // ── Notification templates ────────────────────────────────────────────────
  notificationTemplates: () => '/merchant/notification-templates',
  notificationTemplate: (templateKey: string, channel: string) =>
    `/merchant/notification-templates/${encodeURIComponent(templateKey)}/${encodeURIComponent(channel)}`,
} as const;
